import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Avatar,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Alert,
  Divider,
  FormControl
} from '@mui/material';
import {
  EventBusy as EventBusyIcon,
  WorkOutline as WorkOutlineIcon
} from '@mui/icons-material';
import moment from 'moment';
import { Employee, Schedule } from '../../../pages/Dashboard/Dashboard';

interface Shift {
  shiftName: string;
  shiftId: string;
  notes: string;
  assignedStaff: string[];
  staffNeeded: string;
  startTime: string;
  endTime: string;
  position: string;
  isRecurring: boolean;
  daysOfWeek?: string[];
  scheduleId: string;
  shiftDate?: string;
}

interface RequestOffModalProps {
  open: boolean;
  onClose: () => void;
  shift?: Shift | null;
  currentEmployeeId: string;
  employees: Employee[];
  positions: Record<string, any>[];
  availableShifts?: Shift[];
  onSubmitRequestOff?: (shiftId: string, offDate: string, offReason: string) => void;
  selectedSchedule: Schedule | null;
  selectedDate: string | null;
}

const RequestOffModal: React.FC<RequestOffModalProps> = ({
  open,
  onClose,
  shift,
  currentEmployeeId,
  employees,
  positions,
  availableShifts = [],
  onSubmitRequestOff,
  selectedSchedule,
  selectedDate
}) => {
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [offReason, setOffReason] = useState('');

  // Determine the current shift to work with
  const currentShift = shift || availableShifts.find(s => s.shiftId === selectedShiftId);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (shift) {
        setSelectedShiftId(shift.shiftId);
      } else {
        setSelectedShiftId('');
      }
      setOffReason('');
    }
  }, [open, shift]);

  // Helper function to get position by ID
  const getPosition = (positionId: string) => {
    return positions.find(p => p.id === positionId);
  };

  const formatShiftTime = (timeStr: string) => {
    const [hour, min] = timeStr.split(':').map(Number);
    return moment().hours(hour).minutes(min).format('h:mm A');
  };

  // Get user's shifts (only shifts they're assigned to)
  const userShifts = availableShifts.filter(s =>
    s.assignedStaff?.includes(currentEmployeeId)
  );

  const handleClose = () => {
    setSelectedShiftId('');
    setOffReason('');
    onClose();
  };

  const handleSubmitRequestOff = () => {
    if (currentShift && offReason.trim() && onSubmitRequestOff) {
      onSubmitRequestOff(currentShift.shiftId, selectedDate || moment().format("YYYY-MM-DD"), offReason);
      handleClose();
    }
  };

  const getShiftDisplayText = (shiftData: Shift) => {
    const timeText = `${formatShiftTime(shiftData.startTime)} - ${formatShiftTime(shiftData.endTime)}`;

    if (shiftData.isRecurring && shiftData.daysOfWeek) {
      const days = shiftData.daysOfWeek.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
      return `${shiftData.shiftName} • ${timeText} • ${days}`;
    } else if (shiftData.shiftDate) {
      const dateText = moment(shiftData.shiftDate).format('MMM D, YYYY');
      return `${shiftData.shiftName} • ${timeText} • ${dateText}`;
    } else {
      return `${shiftData.shiftName} • ${timeText}`;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'error.light' }}>
            <EventBusyIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Request Time Off
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {shift ? 'Request to be removed from this shift' : 'Select a shift and request time off'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Shift Selection (only show if no specific shift was provided) */}
        {!shift && (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Your Shift</InputLabel>
              <Select
                value={selectedShiftId}
                label="Select Your Shift"
                onChange={(e) => setSelectedShiftId(e.target.value)}
              >
                {userShifts.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No shifts available
                    </Typography>
                  </MenuItem>
                ) : (
                  userShifts.map(shiftData => (
                    <MenuItem key={shiftData.shiftId} value={shiftData.shiftId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, width: '100%' }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'error.light' }}>
                          <WorkOutlineIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {shiftData.shiftName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {getShiftDisplayText(shiftData)}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
              {userShifts.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                  You need to be assigned to shifts before you can request time off.
                </Typography>
              )}
            </FormControl>

            {selectedShiftId && <Divider sx={{ mb: 3 }} />}
          </>
        )}

        {/* Current Shift Display */}
        {currentShift && (
          <>
            <Alert severity="warning" sx={{ mb: 3 }}>
              This will request to be removed from your scheduled shift. Your manager will need to approve this request.
            </Alert>

            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'error.50', borderColor: 'error.200' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {shift ? 'Shift to Remove' : 'Selected Shift'}
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {currentShift.shiftName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formatShiftTime(currentShift.startTime)} - {formatShiftTime(currentShift.endTime)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={getPosition(currentShift.position)?.positionName || getPosition(currentShift.position)?.name || 'Unknown Position'}
                  size="small"
                />
                {currentShift.isRecurring && (
                  <Chip
                    label="Recurring"
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
                {!currentShift.isRecurring && currentShift.shiftDate && (
                  <Chip
                    label={moment(currentShift.shiftDate).format('MMM D')}
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Show recurring days */}
              {currentShift.isRecurring && currentShift.daysOfWeek && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Repeats on:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {currentShift.daysOfWeek.map(day => (
                      <Chip
                        key={day}
                        label={day.charAt(0).toUpperCase() + day.slice(1, 3)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Show notes if any */}
              {currentShift.notes && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                  Note: {currentShift.notes}
                </Typography>
              )}
            </Paper>

            {/* Reason TextField */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason for Time Off"
              placeholder="Please explain why you need time off for this shift..."
              value={offReason}
              onChange={(e) => setOffReason(e.target.value)}
              required
              helperText="A detailed reason helps your manager process your request faster"
              disabled={!currentShift}
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              Your manager will review this request and notify you of their decision.
            </Alert>
          </>
        )}

        {/* Show message when no shift is selected */}
        {!currentShift && !shift && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'grey.200', mx: 'auto', mb: 2 }}>
              <WorkOutlineIcon sx={{ fontSize: 32, color: 'grey.400' }} />
            </Avatar>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Select a shift to get started
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Choose one of your assigned shifts to request time off
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmitRequestOff}
          variant="contained"
          color="error"
          disabled={!currentShift || !offReason.trim()}
          startIcon={<EventBusyIcon />}
        >
          Submit Request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestOffModal;
